import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Mail, User, Phone, ArrowRight, AlertCircle, MapPin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { resolvePostAuthRoute } from "@/lib/postAuthRoute";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { capitalizeName } from "@/lib/text";
import { useSeo } from "@/lib/seo";

const EVT_TYPES = ["Mariage", "Anniversaire", "Entreprise", "Collectivité / Mairie", "Association", "Baptême / Communion", "Autre"];

export default function Inscription() {
  useSeo({
    title: "Espace client · Créer un compte",
    description: "Créez votre espace client My Animation pour suivre votre événement et personnaliser votre prestation.",
    path: "/inscription",
  });
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    prenom: "", nom: "", email: "", phone: "",
    adresse: "", code_postal: "", ville: "",
    type_evenement: "Mariage", password: "", password2: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.value;
    // Auto-capitalize names/city + lowercase email so admin database stays clean
    let normalized = v;
    if (k === "prenom" || k === "nom" || k === "ville") normalized = capitalizeName(v);
    else if (k === "email") normalized = v.toLowerCase();
    setForm((f) => ({ ...f, [k]: normalized }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password !== form.password2) {
      setErr("Les mots de passe ne correspondent pas.");
      return;
    }
    if (form.password.length < 6) {
      setErr("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    setLoading(true);
    try {
      const u = await register({
        prenom: form.prenom, nom: form.nom, email: form.email, phone: form.phone,
        adresse: form.adresse, code_postal: form.code_postal, ville: form.ville,
        password: form.password, type_evenement: form.type_evenement,
      });
      const dest = await resolvePostAuthRoute(u);
      nav(dest, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-black flex items-center justify-center px-6 py-24 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -left-40 w-[600px] h-[600px] rounded-full bg-[#DCB457]/[0.06] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#DCB457]/[0.04] blur-3xl" />

      <div className="relative w-full max-w-[520px]">
        <Link to="/" className="block text-center mb-10">
          <span className="font-serif-luxe text-3xl md:text-4xl gold-text">My Animation</span>
          <p className="text-[10px] uppercase tracking-[0.32em] text-white/45 mt-2">Espace client</p>
        </Link>

        <div className="card-luxe p-8 md:p-10">
          <h1 className="font-serif-luxe text-3xl md:text-4xl leading-tight">
            Créez votre <span className="italic gold-text">espace client.</span>
          </h1>
          <p className="text-white/60 text-sm mt-2 mb-8">
            Suivez votre événement, partagez vos envies, préparez la soirée parfaite.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field icon={User} label="Prénom *" required value={form.prenom} onChange={set("prenom")} placeholder="Jean" testid="register-prenom" autoComplete="given-name" />
              <Field icon={User} label="Nom *" required value={form.nom} onChange={set("nom")} placeholder="Dupont" testid="register-nom" autoComplete="family-name" />
            </div>
            <Field icon={Phone} label="Téléphone *" required type="tel" value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" testid="register-phone" autoComplete="tel" />
            <Field icon={Mail} type="email" required label="Email *" value={form.email} onChange={set("email")} placeholder="vous@email.fr" testid="register-email" autoComplete="email" />

            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">Adresse postale *</label>
              <AddressAutocomplete
                value={form.adresse}
                onChange={(v) => setForm((f) => ({ ...f, adresse: v }))}
                onSelect={(s) => setForm((f) => ({
                  ...f,
                  adresse: s.street || s.label,
                  code_postal: s.postcode || f.code_postal,
                  ville: s.city || f.ville,
                }))}
                required
                placeholder="12 rue des Lilas"
                testId="register-adresse"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">CP *</label>
                <input
                  type="text"
                  required
                  value={form.code_postal}
                  onChange={set("code_postal")}
                  data-testid="register-cp"
                  autoComplete="postal-code"
                  placeholder="59166"
                  className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition"
                />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">Ville *</label>
                <input
                  type="text"
                  required
                  value={form.ville}
                  onChange={set("ville")}
                  data-testid="register-ville"
                  autoComplete="address-level2"
                  placeholder="Bousbecque"
                  className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">Type d'événement *</label>
              <select
                required
                value={form.type_evenement}
                onChange={set("type_evenement")}
                data-testid="register-evt-type"
                className="w-full bg-black/60 border border-[#DCB457]/25 px-3 py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition"
              >
                {EVT_TYPES.map((t) => <option key={t} className="bg-black">{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field icon={Lock} type="password" required label="Mot de passe *" value={form.password} onChange={set("password")} placeholder="6 caractères min." testid="register-password" autoComplete="new-password" />
              <Field icon={Lock} type="password" required label="Confirmation *" value={form.password2} onChange={set("password2")} placeholder="••••••••" testid="register-password2" autoComplete="new-password" />
            </div>

            {err && (
              <div className="flex items-start gap-2 border border-red-400/40 bg-red-400/5 px-3 py-2 text-xs text-red-300" data-testid="register-error">
                <AlertCircle size={14} className="mt-0.5 shrink-0" strokeWidth={1.5} />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
            >
              {loading ? "Création…" : "Créer mon compte"} <ArrowRight size={16} strokeWidth={1.4} />
            </button>
          </form>

          <p className="text-center text-xs text-white/50 mt-6">
            Déjà inscrit ?{" "}
            <Link to="/connexion" data-testid="register-to-login" className="text-[#DCB457] hover:underline">
              Connectez-vous
            </Link>
          </p>
        </div>

        <p className="text-center text-[10px] uppercase tracking-[0.28em] text-white/40 mt-8">
          <Link to="/" className="hover:text-[#DCB457]">← Retour au site</Link>
        </p>
      </div>
    </main>
  );
}

function Field({ icon: Icon, label, type = "text", value, onChange, placeholder, required, testid, autoComplete }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-white/55 mb-2 block">{label}</label>
      <div className="relative">
        {Icon && <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#DCB457]/70" strokeWidth={1.2} />}
        <input
          type={type}
          required={required}
          value={value}
          onChange={onChange}
          data-testid={testid}
          autoComplete={autoComplete}
          className={`w-full bg-black/60 border border-[#DCB457]/25 ${Icon ? "px-10" : "px-3"} py-3 text-sm text-white focus:border-[#DCB457] focus:outline-none transition`}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
}
